<script setup lang="ts">
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import { useObjectTypesFieldsGroupsLayout } from '@/features/Inventory/ObjectTypes/model/useObjectTypesFieldsGroupsLayout'
import { useTranslate } from '@/shared/lib'
import { type PropType, ref, watch } from 'vue'
import './assets/style.scss'
import { InventTypeFieldSetting } from '@/entities/Inventory/TypeField'
import { InventObjectTypesGroupDelete } from '@/features/Inventory/ObjectTypesGroupDelete'
import { InventObjectTypesGroupModalButton } from '@/features/Inventory/ObjectTypesGroupModal'
import InventObjectTypesFieldsGroupBody from './InventObjectTypesFieldsGroupBody.vue'

const props = defineProps({
  groups: {
    type: Array as PropType<ObjectTypeGroup[]>,
    default: () => [],
  },
  refetch: {
    type: Function,
    required: true,
  },
  isChild: {
    type: Boolean,
    default: false,
  },
  parentType: {
    type: Object as PropType<Record<string, unknown> | null>,
    default: null,
  },
  isSystem: {
    type: Boolean,
    default: false,
  },
  /** Hide Перечни / map / behavior when «Настройка паспорта» tab is not used (passports-config without unified passport). */
  hideSecondaryGroups: {
    type: Boolean,
    default: false,
  },
  /** Корень с ровно одним ребёнком не может иметь собственных полей — кнопку прячем */
  hideAddField: {
    type: Boolean,
    default: false,
  },
})

const model = defineModel<ObjectInventoryField[]>({ default: [] })

const { trs } = useTranslate()

const { groupsLayout, groupsView, displayGroupName } = useObjectTypesFieldsGroupsLayout(
  () => props.groups,
  model,
  trs,
)

const openForm = ref(false)
const editField = ref<ObjectInventoryField | null>(null)

const editSettingsHandler = (field: ObjectInventoryField) => {
  openForm.value = true
  editField.value = field
}

const activeTab = ref<number | string | undefined>(undefined)

watch(
  () => (props.hideSecondaryGroups ? '' : groupsLayout.value.tabGroups.map((g) => g.id).join(',')),
  () => {
    if (props.hideSecondaryGroups) {
      activeTab.value = undefined
      return
    }
    const tabs = groupsLayout.value.tabGroups
    if (!tabs.length) {
      activeTab.value = undefined
      return
    }
    const current = activeTab.value
    const hasCurrent = tabs.some((g) => g.id === current)
    if (current === undefined || !hasCurrent) {
      const first = tabs.find((t) => t.type !== 'HISTORY') ?? tabs[0]
      activeTab.value = first.id as number
    }
  },
  { immediate: true },
)
</script>

<template>
  <div v-if="groupsLayout.primary" class="invent-object-types-fields__primary mb-6">
    <InventObjectTypesFieldsGroupBody
      :group="groupsLayout.primary"
      :fields="model"
      :is-child="isChild"
      :parent-type="parentType"
      :hide-add-field="hideAddField"
      @edit-settings="editSettingsHandler"
    />
  </div>

  <template v-if="groupsLayout.tabGroups.length && !hideSecondaryGroups && !isSystem">
    <VTabs v-model="activeTab" class="mb-4" density="compact">
      <VTab
        v-for="g in groupsLayout.tabGroups"
        :key="g.id"
        :value="g.id"
        :disabled="g.type === 'HISTORY'"
      >
        {{ displayGroupName(g) }}
      </VTab>
    </VTabs>

    <VTabsWindow v-model="activeTab">
      <VTabsWindowItem v-for="g in groupsLayout.tabGroups" :key="g.id" :value="g.id">
        <div class="d-flex align-center flex-wrap justify-end gap-1 mb-4">
          <InventObjectTypesGroupModalButton v-if="g.mode !== 'PROTECTED' && !isChild" :group="g">
            <VBtn density="compact" icon>
              <VIcons type="settings" />
            </VBtn>
          </InventObjectTypesGroupModalButton>

          <InventObjectTypesGroupDelete
            v-if="g.mode === 'DEFAULT'"
            :group="g"
            @removed="() => refetch()"
          />
        </div>

        <InventObjectTypesFieldsGroupBody
          :group="g"
          :fields="model"
          :is-child="isChild"
          :parent-type="parentType"
          :hide-add-field="hideAddField"
          @edit-settings="editSettingsHandler"
        />
      </VTabsWindowItem>
    </VTabsWindow>
  </template>

  <InventTypeFieldSetting
    v-if="editField"
    :groups="groupsView"
    v-model="openForm"
    v-model:field="editField"
  />
</template>
